import * as React from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DateIn from '../Form/Date';
import moment from 'moment';
import { toast } from 'react-toastify';
import SelectX from '../Form/SelectX';
import { fetchAssignees, fetchLeadSources, fetchProjects } from '../CommonFunctions/DropdownLists';

import { get } from '../../api/Config/config';

export default function NotesDownloadPopup({ open, setOpen, watch, control, reset, setValue }) {
    const [downloading, setDownloading] = React.useState(false);
    
    const handleClose = () => {
        setOpen(false)
    }

   const handleDownload = async () => {
    const fromDate = watch('notes_from_date');
    const toDate = watch('notes_to_date');
    const assignedUsers = watch('notes_assigned_users');
    const leadSources = watch('notes_lead_source');
    const project = watch('notes_project');

    if (!fromDate || !toDate) {
        toast.error("Please select both From and To dates");
        return;
    }

    const start = moment(fromDate);
    const end = moment(toDate);
    
    if (end.isBefore(start)) {
        toast.error("To date cannot be before From date");
        return;
    }

    const diffDays = end.diff(start, 'days');
    if (diffDays > 31) {
        toast.error("Date range cannot exceed one month");
        return;
    }

    const formattedFromDate = start.format('YYYY-MM-DD');
    const formattedToDate = end.format('YYYY-MM-DD');
    
    setDownloading(true);
    
    // Show info about the export
    toast.info("Preparing your export...", { duration: 3000 });
    
    try {
        const params = {
            from_date: formattedFromDate,
            to_date: formattedToDate
        };

        if (assignedUsers?.length > 0) {
            params.assigned_users = assignedUsers.map(user => user.id);
        }

        if (leadSources?.length > 0) {
            params.lead_source = leadSources.map(source => source.name);
        }

        if (project?.id) {
            params.project_id = project.id;
        }

        const response = await get(`leads/notes-export`, { params });

        if (response.data && response.data.exports && response.data.exports.length > 0) {
            const { exports, total, files } = response.data;
            
            // Show summary
            toast.success(`Export ready: ${total.toLocaleString()} records in ${files} file(s)`);
            
            // Download each file
            for (const exportItem of exports) {
                const { file, part, records } = exportItem;
                
                // Create a hidden anchor and click it to trigger the download
                const link = document.createElement('a');
                link.href = file;
                
                // Set filename based on part number if multiple files
                if (files > 1) {
                    link.download = `CRM_Leads_Notes_${formattedFromDate}_to_${formattedToDate}_part${part}.xlsx`;
                } else {
                    link.download = `CRM_Leads_Notes_${formattedFromDate}_to_${formattedToDate}.xlsx`;
                }
                
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                // Small delay between downloads to prevent browser issues
                if (files > 1 && part < files) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
            }
            
            handleClose();
        } else {
            toast.error("Failed to generate export file");
        }
    } catch (error) {
        console.error("Export error:", error);
        const errorMessage = error?.data?.error || error?.message || "Error generating export";
        toast.error(errorMessage);
    } finally {
        setDownloading(false);
    }
}

    return (
        <Dialog
            open={open}
            onClose={() => setOpen(false)}
            fullWidth={true}
            maxWidth="sm"
        >
            <Grid display={'flex'} justifyContent={'space-between'} alignItems={'center'} pr={2}>
                <DialogTitle>Export leads with notes</DialogTitle>
                <IconButton onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </Grid>

            <DialogContent dividers>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Note: Maximum one month range is allowed for export.
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <DateIn
                            control={control}
                            name="notes_from_date"
                            label="From Date"
                            value={watch('notes_from_date')}
                        />
                    </Grid>                                        
                    <Grid item xs={12} sm={6}>
                        <DateIn
                            control={control}
                            name="notes_to_date"
                            label="To Date"
                            value={watch('notes_to_date')}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <SelectX
                            ismultiple
                            options={fetchAssignees}
                            label="Assigned User"
                            name={'notes_assigned_users'}
                            defaultValue={watch('notes_assigned_users')}
                            control={control}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <SelectX
                            ismultiple
                            options={fetchLeadSources}
                            label="Lead Source"
                            name={'notes_lead_source'}
                            defaultValue={watch('notes_lead_source')}
                            control={control}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <SelectX
                            options={fetchProjects}
                            label="Project"
                            name={'notes_project'}
                            defaultValue={watch('notes_project')}
                            control={control}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button 
                    onClick={() => {
                        setValue('notes_from_date', null);
                        setValue('notes_to_date', null);
                        setValue('notes_assigned_users', null);
                        setValue('notes_lead_source', null);
                        setValue('notes_project', null);
                    }} 
                    variant="outlined" 
                    color="error"
                >
                    Clear
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                <Button onClick={handleClose} variant="outlined" color="secondary">
                    Cancel
                </Button>
                <Button 
                    onClick={handleDownload} 
                    variant="contained" 
                    color="primary" 
                    disabled={downloading}
                    startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    {downloading ? "Generating..." : "Download"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
